import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Panel, Pill, Avatar } from "@/components/Bits";
import { ArrowLeft, Star, Sparkle, BookOpen, GraduationCap, Translate } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TeacherDetail() {
  const { teacherId } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const r = await api.get(`/teachers/${teacherId}`);
    setTeacher(r.data);
  };
  useEffect(() => { load(); }, [teacherId]);

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/teacher-summary", { teacher_id: teacherId });
      setTeacher(t => ({ ...t, ai_summary: data.text }));
      toast.success("AI bio generated ✨");
    } catch {
      toast.error("AI couldn't generate right now");
    } finally { setGenerating(false); }
  };

  if (!teacher) return <div className="p-6 text-ink-soft">Loading teacher…</div>;

  return (
    <div data-testid="teacher-detail">
      <Link to="/app/teachers" className="inline-flex items-center gap-1 text-sm text-ink-soft mb-4">
        <ArrowLeft size={16} /> Back to team
      </Link>
      <PageHeader eyebrow="Educator Profile" title={teacher.name} subtitle={`${teacher.class_name || "—"} · ${teacher.age_group || "—"}`} />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="At a glance" testid="teacher-glance">
          <div className="flex items-center gap-3">
            <Avatar name={teacher.name} size={64} />
            <div>
              <div className="font-heading font-bold text-lg">{teacher.name}</div>
              <div className="text-xs text-ink-soft">{teacher.email}</div>
              <div className="text-xs text-ink-soft mt-1">{teacher.phone}</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Pill tone="lavender">{teacher.experience_years} yrs</Pill>
            <Pill tone="mustard">{teacher.specialisation || "—"}</Pill>
            {teacher.performance_score && <Pill tone="sage"><Star size={10} weight="fill" className="mr-1" /> {teacher.performance_score}/100</Pill>}
          </div>
        </Panel>

        <Panel title="Qualifications" testid="teacher-quals" className="lg:col-span-2"
          action={<EditButton editing={editing} onClick={() => setEditing(e => !e)} />}>
          {editing ? (
            <EditForm teacher={teacher} onSaved={(t) => { setTeacher(t); setEditing(false); }} />
          ) : (
            <>
              <div className="mb-3">
                <div className="text-xs text-ink-soft mb-1">Qualifications</div>
                <div className="flex flex-wrap gap-1">
                  {(teacher.qualifications || []).map(q => <Pill key={q} tone="terracotta"><GraduationCap size={10} weight="bold" className="mr-1" /> {q}</Pill>)}
                  {(teacher.qualifications || []).length === 0 && <span className="text-xs text-ink-soft">Not added yet.</span>}
                </div>
              </div>
              <div className="mb-3">
                <div className="text-xs text-ink-soft mb-1">Skills</div>
                <div className="flex flex-wrap gap-1">
                  {(teacher.skills || []).map(sk => <Pill key={sk} tone="mustard">{sk}</Pill>)}
                  {(teacher.skills || []).length === 0 && <span className="text-xs text-ink-soft">Not added yet.</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-ink-soft mb-1">Languages</div>
                <div className="flex flex-wrap gap-1">
                  {(teacher.languages || []).map(l => <Pill key={l} tone="sage"><Translate size={10} weight="bold" className="mr-1" /> {l}</Pill>)}
                  {(teacher.languages || []).length === 0 && <span className="text-xs text-ink-soft">Not added yet.</span>}
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>

      <Panel title="AI-generated bio" className="mt-6" testid="teacher-ai-bio" action={(
        <button onClick={generateSummary} disabled={generating} data-testid="generate-bio-btn"
          className="px-3 py-1.5 rounded-full bg-terracotta text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
          <Sparkle weight="duotone" /> {generating ? "Drafting…" : teacher.ai_summary ? "Regenerate" : "Generate bio"}
        </button>
      )}>
        {teacher.ai_summary ? (
          <div className="text-sm leading-relaxed text-ink whitespace-pre-wrap">{teacher.ai_summary}</div>
        ) : (
          <div className="text-sm text-ink-soft">No bio yet. Tap "Generate bio" to let MangoCubs AI write a warm, professional bio using the qualifications, skills, and experience.</div>
        )}
      </Panel>
    </div>
  );
}

function EditButton({ editing, onClick }) {
  return (
    <button data-testid="teacher-edit-btn" onClick={onClick} className="text-xs text-terracotta font-semibold">{editing ? "Cancel" : "Edit"}</button>
  );
}

function EditForm({ teacher, onSaved }) {
  const [skills, setSkills] = useState((teacher.skills || []).join(", "));
  const [qualifications, setQualifications] = useState((teacher.qualifications || []).join(", "));
  const [languages, setLanguages] = useState((teacher.languages || []).join(", "));
  const [score, setScore] = useState(teacher.performance_score || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        skills: skills.split(",").map(s => s.trim()).filter(Boolean),
        qualifications: qualifications.split(",").map(s => s.trim()).filter(Boolean),
        languages: languages.split(",").map(s => s.trim()).filter(Boolean),
        performance_score: score ? Number(score) : undefined,
      };
      const { data } = await api.patch(`/teachers/${teacher.teacher_id}`, payload);
      toast.success("Saved");
      onSaved(data);
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="teacher-edit-form">
      <div>
        <label className="text-xs font-semibold text-ink-soft">Qualifications (comma separated)</label>
        <input data-testid="edit-quals" value={qualifications} onChange={e => setQualifications(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-soft">Skills (comma separated)</label>
        <input data-testid="edit-skills" value={skills} onChange={e => setSkills(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-soft">Languages spoken</label>
        <input data-testid="edit-languages" value={languages} onChange={e => setLanguages(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" placeholder="English, Hindi, Tamil" />
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-soft">Performance score (0-100)</label>
        <input data-testid="edit-score" type="number" min={0} max={100} value={score} onChange={e => setScore(e.target.value)} className="w-full h-11 px-3 rounded-xl bg-paper-alt border border-border" />
      </div>
      <button data-testid="teacher-save-btn" disabled={saving} onClick={save} className="px-4 py-2 rounded-full bg-terracotta text-white text-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
    </div>
  );
}
